import { setItemVisual } from './icons';
import { Inventory, type ItemId, ITEMS, stackLimit } from '../game/inventory';
import { DragController } from './drag';
import { SK, btnCss, kbd, labelCss, redHover, injectSkinCss } from './skin';
import { buildShell, splitBody, paneHead, type Shell } from './shell';

// The storage screen. The whole interaction model is "YOUR inventory <-> STORAGE": a corpse, a Base
// Claim, a chest, any interactable, are all just a slot-based Inventory shown on the LEFT; your own
// inventory grid (6x6) is on the RIGHT. Each CELL is one stack. Move a stack between panes:
//   - drag a cell onto the other pane (shared DragController; you can also drag onto a hotbar slot), or
//   - RIGHT-CLICK a cell: that arms a one-second timer (a green radial fills) and then it transfers.
//     You do NOT hold the button. Hold right and drag ACROSS several cells to arm each of them.

const HOLD_MS = 200; // short arm so each swept cell commits almost instantly ("soon as the timer moves, move it over")
const RING_GOOD = 'rgba(111,179,95,0.95)'; // SK.good with the arm-ring's alpha (conic-gradient needs rgba)
const INV_COLS = 6;
const INV_ROWS = 4; // player backpack is 6x4 = 24 slots (matches GRID_SIZE / the loadout)

interface Hold {
  index: number; // slot index of the armed stack
  fromStorage: boolean;
  cell: HTMLDivElement;
  ring: HTMLDivElement;
  t0: number;
  done: boolean;
}

export interface StorageOpts {
  title: string;
  cols: number; // storage grid width
  rows: number; // storage grid height
  storage: Inventory;
  onClose?: () => void;
  onWorldDrop?: (id: ItemId, amount: number) => void; // drag a stack out of the LEFT pane onto the ground (e.g. dump a crate)
  pos?: { x: number; z: number }; // world XZ of the container -- when set, walking past closeRange auto-closes the UI
  closeRange?: number;
  accept?: ReadonlySet<ItemId>; // if set, the LEFT pane only accepts these item ids (e.g. baseclaim = wood/stone/metal/armor)
  onSort?: () => void; // shows a SORT button: merge + alphabetize the LEFT pane (server-side for shared containers)
  // TAKE-ONLY body loot (MP sleeper): the LEFT pane is server-owned, so a take fires this hook with the exact
  // storage SLOT instead of moving the stack locally, and pushing INTO the pane is blocked. The server grants
  // the item (client-side that arrives as a normal inventory add). Unset = the normal two-way move behaviour.
  onTake?: (slot: number) => void;
  // Server-owned SHARED container (chest/locker/fridge): set ALONGSIDE onTake to make the LEFT pane 2-WAY. A put
  // fires this with the item + count (the client already removed it from the player inv, which is client-owned);
  // the server stores it. With onTake set but onPut UNSET, the pane stays take-only (a sleeper body).
  onPut?: (id: ItemId, n: number) => void;
  // Optional header control (e.g. a cooking station's ON/OFF switch). `label()` is re-read after each click.
  // `on?()` (optional) tells the STATUS card whether the machine is running (state dot + toggle colour).
  headerButton?: { label: () => string; onClick: () => void; on?: () => boolean };
  // Optional live machine readout for the STATUS card's ETA + progress rows (spec section 4). Re-read on
  // every repaint. Unset = the card shows just State / Fuel / Cooking (the data the view can compute itself).
  machineStatus?: () => { etaLabel?: string; progress01?: number };
  // TYPED-SLOT LEFT PANE (e.g. the campfire's FUEL / INPUT / OUTPUT). When set, the LEFT pane renders as these
  // labelled sections (with down-arrows + the CONTROLS button at the bottom) instead of a raw cols x rows grid.
  sections?: ReadonlyArray<{ label: string; slots: number[] }>;
  // Per-slot ROLE gate for a typed left pane: which item ids a given storage slot will hold. Player moves INTO the
  // pane auto-route to the first slot whose role accepts the item; a slot whose role rejects everything is take-only.
  slotAccept?: (slot: number, id: ItemId) => boolean;
}

const STATUS_W = 260; // spec: machine right pane is a `1fr STATUS_W` grid (flow + status card)
const SLOT = 64;      // --ox-slot: one slot spec everywhere

export class StorageView {
  private readonly shell: Shell;
  private readonly rightGrid: HTMLDivElement;   // YOUR inventory grid (in the LEFT split pane)
  private readonly containerPane: HTMLDivElement; // the RIGHT split pane; rebuilt per open (plain grid or machine)
  private leftGrid!: HTMLDivElement;             // the container's storage grid / machine flow host (set in open)
  private statusHost: HTMLDivElement | null = null; // the machine status card body, repainted on rerender
  private opts: StorageOpts | null = null;
  onBackdropClose?: () => void; // set by main: a click/right-click on empty backdrop closes the panel

  // Right-click-to-arm transfers. A single rAF loop advances every armed cell so one finishing does
  // not cancel the others (that is what makes drag-across-multiple work).
  private rmbHeld = false;
  private holds: Hold[] = [];
  private unsubStorage: (() => void) | null = null; // live left-pane subscription to the open container's inventory
  private holdRaf = 0;
  private ticking = false; // true while holdTick is iterating (blocks a re-entrant repaint from tearing down holds)

  constructor(private readonly inv: Inventory, private readonly drag: DragController) {
    injectSkinCss();
    // The shared 1240 shell (Body layout B split). Title/close key are set per open (E for world containers).
    this.shell = buildShell({
      title: 'Storage', closeKey: 'E', bodyPad: false,
      hintsHtml: `Drag &middot; ${kbd('Shift')}-click to send &middot; double-click all &middot; middle/${kbd('Ctrl')}-click half &middot; ${kbd('Alt')}-click one`,
    });
    this.shell.scrim.dataset.drop = 'world'; // releasing a drag on empty backdrop (outside both panes) = drop to ground
    this.shell.win.dataset.drop = 'panel';   // releasing over the window chrome is a no-op (can't fall through to 'world')
    const outsideTap = (e: MouseEvent): boolean => e.target === this.shell.scrim && !this.drag.dragging;
    this.shell.scrim.addEventListener('contextmenu', (e) => { e.preventDefault(); if (outsideTap(e)) this.onBackdropClose?.(); });
    this.shell.scrim.addEventListener('mouseup', (e) => { if (e.button === 0 && outsideTap(e)) this.onBackdropClose?.(); });

    const { left, right } = splitBody(this.shell);
    // LEFT pane = YOUR inventory, always (owner ask). The container/machine is always on the RIGHT.
    left.appendChild(paneHead('Your Inventory', ''));
    this.rightGrid = document.createElement('div');
    this.rightGrid.dataset.drop = 'inventory';
    left.appendChild(this.rightGrid);
    this.containerPane = right;

    document.body.appendChild(this.shell.scrim);
    document.addEventListener('mouseup', (e) => { if (e.button === 2) this.rmbHeld = false; });
    window.addEventListener('blur', () => { this.rmbHeld = false; }); // a release outside the window won't fire mouseup
  }

  // Build the RIGHT split pane for the container being opened: a plain storage grid (chest/corpse/claim)
  // or, for a typed machine (sections), a `1fr STATUS_W` flow + status card. Sets this.leftGrid to the
  // grid/flow host that paint()/paintSectioned() render into.
  private buildContainerPane(opts: StorageOpts): void {
    const host = this.containerPane;
    host.replaceChildren();
    this.statusHost = null;
    if (opts.sections) {
      const mg = document.createElement('div');
      mg.style.cssText = css('display:grid', `grid-template-columns:1fr ${STATUS_W}px`, 'gap:24px', 'min-height:0', 'align-items:start');
      this.leftGrid = document.createElement('div');
      this.leftGrid.dataset.drop = 'storage';
      mg.appendChild(this.leftGrid);
      mg.appendChild(this.buildStatusCard(opts));
      host.appendChild(mg);
    } else {
      // Plain container: an optional head row (Sort and/or a station ON/OFF button), then the grid.
      if (opts.onSort || opts.headerButton) {
        const headRow = document.createElement('div');
        headRow.style.cssText = css('display:flex', 'align-items:center', 'gap:8px', 'margin-bottom:12px', 'flex:none');
        if (opts.headerButton) {
          const hb = document.createElement('button');
          hb.textContent = opts.headerButton.label();
          hb.style.cssText = btnCss('secondary', true);
          hb.addEventListener('click', () => { opts.headerButton!.onClick(); hb.textContent = opts.headerButton!.label(); });
          redHover(hb);
          headRow.appendChild(hb);
        }
        if (opts.onSort) {
          const sb = document.createElement('button');
          sb.textContent = 'Sort';
          sb.style.cssText = btnCss('secondary', true);
          sb.addEventListener('click', () => { opts.onSort?.(); this.rerender(); });
          redHover(sb);
          headRow.appendChild(sb);
        }
        host.appendChild(headRow);
      }
      this.leftGrid = document.createElement('div');
      this.leftGrid.dataset.drop = 'storage';
      host.appendChild(this.leftGrid);
    }
  }

  // The machine STATUS card (spec section 4): State (dot), Fuel remaining, Cooking count, an optional
  // ETA/progress (from opts.machineStatus if the opener wired it), a hairline, then the toggle. Its body
  // is repainted on rerender via this.statusHost so live state tracks the machine.
  private buildStatusCard(opts: StorageOpts): HTMLDivElement {
    const card = document.createElement('div');
    card.style.cssText = css('background:rgba(0,0,0,0.22)', `border:1px solid ${SK.border}`, `border-radius:${SK.rMd}`,
      'padding:16px', 'display:flex', 'flex-direction:column', 'gap:12px', 'height:max-content', `width:${STATUS_W}px`);
    const label = document.createElement('div'); label.textContent = 'STATUS'; label.style.cssText = labelCss();
    this.statusHost = document.createElement('div');
    this.statusHost.style.cssText = css('display:flex', 'flex-direction:column', 'gap:12px');
    card.append(label, this.statusHost);
    this.paintStatus(opts);
    return card;
  }

  private paintStatus(opts: StorageOpts): void {
    const host = this.statusHost;
    if (!host || !opts.sections) return;
    host.replaceChildren();
    const on = opts.headerButton?.on?.() ?? null;
    const krow = (k: string, valHtml: string): HTMLDivElement => {
      const r = document.createElement('div');
      r.style.cssText = css('display:flex', 'justify-content:space-between', 'align-items:center', 'gap:10px', `font-size:${SK.fsSmall}`);
      r.innerHTML = `<span style="color:${SK.faint}">${k}</span><span style="color:${SK.text};font-variant-numeric:tabular-nums;display:flex;align-items:center;gap:8px">${valHtml}</span>`;
      return r;
    };
    if (on !== null) host.appendChild(krow('State', `<span style="width:8px;height:8px;border-radius:50%;background:${on ? SK.good : SK.faint};${on ? `box-shadow:0 0 8px ${SK.accentGlow}` : ''}"></span>${on ? 'Lit' : 'Unlit'}`));
    // Fuel = units in the section labelled Fuel; Cooking = filled input slots / total.
    const fuelSec = opts.sections.find((s) => /fuel/i.test(s.label));
    const inSec = opts.sections.find((s) => /input/i.test(s.label));
    if (fuelSec) { const n = fuelSec.slots.reduce((a, i) => a + (opts.storage.stackAt(i)?.n ?? 0), 0); host.appendChild(krow('Fuel', n > 0 ? `${n}` : 'Empty')); }
    if (inSec) { const filled = inSec.slots.filter((i) => opts.storage.stackAt(i)).length; host.appendChild(krow('Cooking', `${filled} / ${inSec.slots.length}`)); }
    const ms = opts.machineStatus?.();
    if (ms?.etaLabel) host.appendChild(krow(on ? 'Ready in' : 'Idle', ms.etaLabel));
    if (ms && ms.progress01 != null) {
      const bar = document.createElement('div'); bar.style.cssText = css('height:4px', 'border-radius:2px', 'background:rgba(255,255,255,0.06)', 'overflow:hidden');
      const fill = document.createElement('div'); fill.style.cssText = css('height:100%', `width:${Math.round(Math.max(0, Math.min(1, ms.progress01)) * 100)}%`, `background:${SK.accent}`);
      bar.appendChild(fill); host.appendChild(bar);
    }
    if (opts.headerButton) {
      const hr = document.createElement('div'); hr.style.cssText = css('border-top:1px solid ' + SK.border, 'margin:2px 0');
      host.appendChild(hr);
      // Machine toggle: secondary button + a leading state dot. Never a solid green OFF button.
      const btn = document.createElement('button');
      btn.style.cssText = css(btnCss('secondary'), 'width:100%');
      btn.innerHTML = `<span style="width:8px;height:8px;border-radius:50%;flex:none;background:${on ? SK.accent : SK.faint};${on ? `box-shadow:0 0 8px ${SK.accentGlow}` : ''}"></span>${opts.headerButton.label()}`;
      btn.addEventListener('click', () => { opts.headerButton!.onClick(); this.rerender(); });
      host.appendChild(btn);
    }
  }

  get isOpen(): boolean {
    return this.opts !== null;
  }

  open(opts: StorageOpts): void {
    this.unsubStorage?.();
    this.opts = opts;
    // LIVE LEFT PANE: subscribe to the CONTAINER inventory we are showing, so anything that mutates it while the
    // panel is open repaints immediately -- a cooking campfire producing charcoal, a server cView refresh landing
    // in the shared-container mirror, another player's take. Before this, only the PLAYER inventory emit repainted,
    // so a station's progress was invisible until the panel was closed and reopened (audit G1).
    this.unsubStorage = opts.storage.subscribe(() => this.rerender());
    // The container name is the WINDOW title (shell header). E is the close key for every world container.
    this.shell.setTitle(opts.title);
    this.shell.setSub(opts.sections ? 'Wood + raw food/ore in, cooked food + metal out.' : null);
    this.buildContainerPane(opts); // rebuild the RIGHT pane (plain grid vs machine flow + status card)
    this.shell.scrim.style.display = 'flex';
    this.rerender();
  }

  close(): void {
    if (!this.opts) return;
    const onClose = this.opts.onClose;
    this.unsubStorage?.();
    this.unsubStorage = null;
    this.clearHolds();
    this.rmbHeld = false;
    this.opts = null;
    this.shell.scrim.style.display = 'none';
    onClose?.();
  }

  refresh(): void {
    if (this.opts) this.rerender();
  }

  // Walk-away auto-close: if the open container has a world position and the player moved past its range, shut it
  // (no infinite-range looting). Fires the normal close path (onClose despawns an emptied crate etc.).
  autoCloseIfFar(px: number, pz: number): void {
    if (!this.opts || !this.opts.pos || this.opts.closeRange == null) return;
    const dx = this.opts.pos.x - px, dz = this.opts.pos.z - pz;
    if (dx * dx + dz * dz > this.opts.closeRange * this.opts.closeRange) this.close();
  }

  // Drop targets call these (via the central DragController handlers in main.ts). `index` is the exact
  // stack's slot; without it (legacy path) the first matching stack moves.
  putToStorage(id: ItemId, index?: number): void { if (this.opts?.onTake && !this.opts.onPut) return; this.moveStackAt(this.findIndex(this.inv, id, index), false); this.rerender(); } // take-only body: no push in; server container (onPut): moveStackAt routes to the server
  takeFromStorage(id: ItemId, index?: number): void { if (this.opts) this.moveStackAt(this.findIndex(this.opts.storage, id, index), true); this.rerender(); }

  // Dump a stack from the LEFT (storage) pane onto the ground -- dragged out of the panel. Spawns the world drop
  // via the opener's callback; if that empties the storage (an emptied crate) the panel closes, which fires
  // onClose -> the crate despawns. Only meaningful for containers that supplied onWorldDrop (crates).
  dropToWorld(id: ItemId, index?: number): void {
    if (!this.opts || !this.opts.onWorldDrop) return;
    const i = this.findIndex(this.opts.storage, id, index);
    const s = i >= 0 ? this.opts.storage.stackAt(i) : null;
    if (!s) return;
    const n = this.opts.storage.takeFromSlot(i, s.n);
    if (n <= 0) return;
    this.opts.onWorldDrop(id, n);
    if (this.opts.storage.isEmpty()) this.close(); // emptied onto the ground -> shut + despawn the crate (onClose)
    else this.rerender();
  }

  // Push n of an item INTO the open storage from outside the two panes (a hotbar stack dragged in).
  // Returns how many actually fit (the caller removes exactly that from the source).
  putStack(id: ItemId, n: number): number {
    if (!this.opts || n <= 0) return 0;
    if (this.opts.onTake) {
      if (!this.opts.onPut) return 0; // take-only body loot: nothing goes IN
      if (this.opts.accept && !this.opts.accept.has(id)) return 0;
      this.opts.onPut(id, n); // server-owned container: the server stores it; the caller removes n from the source
      return n;
    }
    if (this.opts.accept && !this.opts.accept.has(id)) return 0; // filtered container: not allowed in
    if (this.opts.slotAccept) { // typed pane: route to the role slot instead of first-fit
      const dst = this.findSlotFor(id);
      if (dst < 0) return 0;
      const put = this.opts.storage.addToSlot(dst, id, n);
      if (put > 0) this.rerender();
      return put;
    }
    const put = Math.min(n, this.opts.storage.room(id));
    if (put > 0) { this.opts.storage.add(id, put); this.rerender(); }
    return put;
  }

  // Resolve a payload to a concrete slot: trust the index if it still holds that item, else first match.
  private findIndex(inv: Inventory, id: ItemId, index?: number): number {
    if (index != null && inv.stackAt(index)?.id === id) return index;
    for (let i = 0; i < inv.size; i++) if (inv.stackAt(i)?.id === id) return i;
    return -1;
  }

  private rerender(): void {
    if (!this.opts) return;
    // A right-drag sweep is in flight. Defer the repaint -- INCLUDING the async server cView echo that arrives after
    // each server-pane take -- so it can't clearHolds()+swap the cell DOM out from under the still-arming rings (the
    // bug where the sweep died after one cell). holdTick's batch-drain repaints once when the sweep ends, and any
    // late echo repaints then too (holds already empty). `ticking` covers the synchronous re-entrancy; `holds.length`
    // covers the asynchronous one.
    if (this.ticking || this.holds.length) return;
    this.clearHolds();
    if (this.opts.sections) { this.paintSectioned(this.leftGrid, this.opts.storage, this.opts.sections); this.paintStatus(this.opts); }
    else this.paint(this.leftGrid, this.opts.storage, this.opts.cols, this.opts.rows, true);
    this.paint(this.rightGrid, this.inv, INV_COLS, INV_ROWS, false);
  }

  // Render the LEFT pane as labelled typed-slot sections (FUEL / INPUT / OUTPUT ...) with a down-arrow between each
  // and, if a headerButton was supplied, a CONTROLS row with a big ON/OFF button at the bottom. Cells reuse the same
  // filledCell/emptyCell machinery as the grid path, so drag / double-click / right-click-hold all still work.
  private paintSectioned(host: HTMLDivElement, inv: Inventory, sections: ReadonlyArray<{ label: string; slots: number[] }>): void {
    host.replaceChildren();
    host.style.cssText = css('display:flex', 'flex-direction:column', 'gap:12px', 'min-width:0');
    host.dataset.drop = 'storage';
    sections.forEach((sec, si) => {
      if (si > 0) host.appendChild(sectionArrow());
      host.appendChild(sectionLabel(sec.label));
      const row = document.createElement('div');
      row.style.cssText = css('display:flex', 'gap:8px', 'align-items:center');
      for (const i of sec.slots) {
        const s = inv.stackAt(i);
        const cell = s ? this.filledCell(s.id, s.n, i, true) : emptyCell();
        cell.dataset.drop = 'storage';
        cell.dataset.slot = String(i);
        row.appendChild(cell);
      }
      // Multi-slot INPUT/FUEL sections get a SPLIT-EVENLY button (owner QoL): redistribute what's in the
      // section's slots into even stacks so a furnace smelts all lanes in parallel instead of one big pile.
      if (sec.slots.length >= 2 && sec.slots.some((i) => inv.stackAt(i))) {
        const split = document.createElement('button');
        split.textContent = '⇌ even';
        split.title = 'Split this row evenly across its slots';
        split.style.cssText = btnCss('secondary', true);
        split.addEventListener('click', () => { this.splitSectionEvenly(sec.slots); this.rerender(); });
        redHover(split);
        row.appendChild(split);
      }
      host.appendChild(row);
    });
    // The machine ON/OFF toggle lives in the STATUS card now (spec section 4), not inline in the flow.
  }

  private paint(grid: HTMLDivElement, inv: Inventory, cols: number, rows: number, isStorage: boolean): void {
    grid.replaceChildren();
    grid.style.cssText = css('display:grid', `grid-template-columns:repeat(${cols},${SLOT}px)`, `gap:8px`, 'flex:none');
    grid.dataset.drop = isStorage ? 'storage' : 'inventory';
    const total = cols * rows;
    for (let i = 0; i < total; i++) {
      const s = inv.stackAt(i);
      const cell = s ? this.filledCell(s.id, s.n, i, isStorage) : emptyCell();
      // Per-slot drop target so a drag can land on THIS exact slot (free placement / swap), not just the pane.
      cell.dataset.drop = isStorage ? 'storage' : 'inventory';
      cell.dataset.slot = String(i);
      grid.appendChild(cell);
    }
  }

  // Redistribute one typed SECTION's contents evenly across its slots. Multiple item types partition the
  // slots between them (proportionally, at least one slot each); leftovers that don't fit (stack caps) fall
  // back into any section slot, then the player inventory -- nothing is ever lost.
  private splitSectionEvenly(slots: number[]): void {
    const st = this.opts?.storage;
    if (!st) return;
    const tallies = new Map<ItemId, number>();
    for (const i of slots) {
      const s = st.stackAt(i);
      if (s) tallies.set(s.id, (tallies.get(s.id) ?? 0) + st.takeFromSlot(i, s.n));
    }
    const types = [...tallies.entries()];
    if (!types.length) return;
    let si = 0;
    types.forEach(([id, total], ti) => {
      const share = Math.max(1, Math.round((slots.length - si) / (types.length - ti)));
      const mine = slots.slice(si, si + share);
      si += share;
      let left = total;
      mine.forEach((slot, k) => { left -= st.addToSlot(slot, id, Math.ceil(left / (mine.length - k))); });
      for (const sl of slots) { if (left <= 0) break; left -= st.addToSlot(sl, id, left); }
      if (left > 0) this.inv.add(id, left);
    });
  }

  // Reorder a stack WITHIN the container (drag one storage slot onto another): move / swap / merge. Blocked
  // for server-owned body loot (onTake), which the client may not rearrange.
  moveWithinStorage(from: number, to: number): void {
    if (!this.opts || this.opts.onTake || this.opts.slotAccept) return; // typed slots (fuel/input/output) are not free to rearrange
    this.opts.storage.moveOrSwap(from, to);
    this.rerender();
  }

  // For a typed left pane: the first storage slot whose ROLE accepts `id` (prefer a matching partial stack to merge,
  // else the first empty slot that role permits). -1 = no slot will take it (e.g. an output-only item). No mutation.
  private findSlotFor(id: ItemId): number {
    const acc = this.opts?.slotAccept, st = this.opts?.storage;
    if (!acc || !st) return -1;
    for (let i = 0; i < st.size; i++) { const s = st.stackAt(i); if (s && s.id === id && s.n < stackLimit(id) && acc(i, id)) return i; }
    for (let i = 0; i < st.size; i++) { if (!st.stackAt(i) && acc(i, id)) return i; }
    return -1;
  }

  private filledCell(id: ItemId, n: number, index: number, isStorage: boolean): HTMLDivElement {
    const cell = emptyCell();
    const iconed = setItemVisual(cell, id, tint(ITEMS[id].color));
    // Dark ink stays hardcoded: it sits on the item's own light tint() swatch, not on a UI surface.
    const inkCss = iconed ? `color:${SK.textHi};text-shadow:0 1px 3px rgba(0,0,0,0.95)` : 'color:#0c0f14';
    cell.style.borderColor = SK.borderStrong;
    cell.style.cursor = 'grab';

    const name = document.createElement('div');
    name.textContent = ITEMS[id].name;
    name.style.cssText = css('font-size:10px', 'line-height:1.1', 'padding:4px 4px 0', inkCss, 'font-weight:600', 'pointer-events:none', 'word-break:break-word');
    const cnt = document.createElement('div');
    cnt.textContent = String(n);
    cnt.style.cssText = css('position:absolute', 'right:4px', 'bottom:3px', 'font-size:15px', 'font-weight:700', 'font-variant-numeric:tabular-nums', inkCss, 'pointer-events:none');
    cell.appendChild(name);
    cell.appendChild(cnt);

    cell.addEventListener('mousedown', (e) => {
      if (e.button === 0) {
        // SHIFT+CLICK: quick-transfer with the same green radial as right-click-arm (owner: not instant).
        if (e.shiftKey) { e.preventDefault(); this.beginHold(cell, index, isStorage); return; }
        // CTRL+CLICK: send HALF across. ALT+CLICK: send exactly ONE (stack splitting without a middle mouse).
        if (e.ctrlKey) { e.preventDefault(); this.moveStackAt(index, isStorage, 0.5); this.rerender(); return; }
        if (e.altKey) { e.preventDefault(); this.moveStackAt(index, isStorage, 1, 1); this.rerender(); return; }
        this.drag.beginCandidate({ source: isStorage ? 'storage' : 'inventory', id, index, n }, `${ITEMS[id].name} (${n})`, e);
      } else if (e.button === 1) {
        e.preventDefault(); // MIDDLE-CLICK: send HALF the stack across (a quick split)
        this.moveStackAt(index, isStorage, 0.5);
        this.rerender();
      } else if (e.button === 2) {
        e.preventDefault();
        this.rmbHeld = true;
        this.beginHold(cell, index, isStorage);
      }
    });
    cell.addEventListener('dblclick', (e) => { if (e.button === 0) { this.drag.cancel(); this.moveStackAt(index, isStorage); this.rerender(); } }); // DOUBLE-CLICK: send the whole stack across instantly
    cell.addEventListener('mouseenter', (e) => {
      if (!(e.buttons & 2)) { this.rmbHeld = false; return; } // right button no longer physically down
      if (this.rmbHeld) this.beginHold(cell, index, isStorage);
    });
    return cell;
  }

  // --- right-click-to-arm transfers ---

  private beginHold(cell: HTMLDivElement, index: number, fromStorage: boolean): void {
    if (cell.dataset.holding) return;
    cell.dataset.holding = '1';
    const ring = document.createElement('div');
    ring.style.cssText = css(
      'position:absolute', 'inset:0', `border-radius:${SK.rSm}`, 'pointer-events:none',
      `background:conic-gradient(${RING_GOOD} 0deg, rgba(0,0,0,0.35) 0deg)`,
    );
    cell.appendChild(ring);
    this.holds.push({ index, fromStorage, cell, ring, t0: performance.now(), done: false });
    if (!this.holdRaf) this.holdRaf = requestAnimationFrame((t) => this.holdTick(t));
  }

  private holdTick(t: number): void {
    // moveStackAt mutates the player inventory, whose emit() reaches Hud.refresh -> storage.refresh ->
    // rerender. Guard that re-entrancy so a finishing cell can't clear the array (and the other
    // still-arming cells) mid-pass; the repaint is deferred to the batch-drain below.
    this.ticking = true;
    let allDone = true;
    for (const h of this.holds) {
      if (h.done) continue;
      const p = Math.min(1, (t - h.t0) / HOLD_MS);
      h.ring.style.background = `conic-gradient(${RING_GOOD} ${(p * 360).toFixed(0)}deg, rgba(0,0,0,0.35) 0deg)`;
      if (p >= 1) { h.done = true; this.moveStackAt(h.index, h.fromStorage); }
      else allDone = false;
    }
    this.ticking = false;
    if (this.holds.length && !allDone) {
      this.holdRaf = requestAnimationFrame((tt) => this.holdTick(tt));
    } else {
      this.holdRaf = 0;
      if (this.holds.length) { this.clearHolds(); this.rerender(); } // batch drained -> one repaint
    }
  }

  private clearHolds(): void {
    for (const h of this.holds) { h.ring.remove(); delete h.cell.dataset.holding; }
    this.holds = [];
    if (this.holdRaf) { cancelAnimationFrame(this.holdRaf); this.holdRaf = 0; }
  }

  // Move the stack in slot `index` of the source pane to the other pane (no repaint; callers repaint).
  // fraction<1 moves a slice (middle-click = half); exact>0 moves exactly that many (alt-click = 1).
  // Respects the destination's slot capacity, so nothing is lost when the target is near full.
  private moveStackAt(index: number, fromStorage: boolean, fraction = 1, exact = 0): void {
    if (!this.opts || index < 0) return;
    // SERVER-OWNED LEFT pane: takes/puts are server requests, not local moves.
    //  - take (fromStorage): fire onTake(slot); the grant arrives later as a normal inventory add.
    //  - put (!fromStorage): only for a 2-way container (onPut set) -- remove from the player (client-authoritative
    //    inv) and tell the server; the container view refreshes from the server's reply. A take-only body ignores puts.
    if (this.opts.onTake) {
      if (fromStorage) { this.opts.onTake(index); return; }
      if (this.opts.onPut) {
        const s = this.inv.stackAt(index);
        if (!s) return;
        if (this.opts.accept && !this.opts.accept.has(s.id)) return; // filtered container (dropbox/baseclaim)
        const want = exact > 0 ? Math.min(exact, s.n) : fraction < 1 ? Math.ceil(s.n * fraction) : s.n;
        const took = this.inv.takeFromSlot(index, want);
        if (took > 0) this.opts.onPut(s.id, took);
      }
      return;
    }
    const from = fromStorage ? this.opts.storage : this.inv;
    const to = fromStorage ? this.inv : this.opts.storage;
    const s = from.stackAt(index);
    if (!s) return;
    if (!fromStorage && this.opts.accept && !this.opts.accept.has(s.id)) return; // filtered container: this item isn't allowed in
    const want = exact > 0 ? Math.min(exact, s.n) : fraction < 1 ? Math.ceil(s.n * fraction) : s.n; // half-split rounds up
    // TYPED LEFT PANE (campfire): a move INTO the pane routes to the slot whose ROLE accepts this item (fuel -> fuel
    // slot, raw food -> input slot), never first-fit. -1 = no slot takes it, so nothing moves. Taking OUT is normal.
    if (!fromStorage && this.opts.slotAccept) {
      const dst = this.findSlotFor(s.id);
      if (dst < 0) return;
      const put = this.opts.storage.addToSlot(dst, s.id, want);
      if (put > 0) from.takeFromSlot(index, put);
      return;
    }
    const n = Math.min(want, to.room(s.id));
    if (n > 0) { from.takeFromSlot(index, n); to.add(s.id, n); }
  }
}

// --- styling helpers ---

function css(...lines: string[]): string {
  return lines.join(';');
}

function cssHex(hex: number): string {
  return '#' + hex.toString(16).padStart(6, '0');
}

// A washed, lighter shade of the item colour so the dark count/name text reads on top.
function tint(hex: number): string {
  const r = (hex >> 16) & 0xff, g = (hex >> 8) & 0xff, b = hex & 0xff;
  const mix = (c: number) => Math.round(c * 0.62 + 255 * 0.38);
  return cssHex((mix(r) << 16) | (mix(g) << 8) | mix(b));
}

function emptyCell(): HTMLDivElement {
  const cell = document.createElement('div');
  cell.style.cssText = css(
    'position:relative', `width:${SLOT}px`, `height:${SLOT}px`, 'flex:none', `border-radius:${SK.rMd}`,
    `border:1px solid ${SK.border}`, `background:${SK.raised}`,
    'overflow:hidden', 'user-select:none',
  );
  return cell;
}

// A typed-section heading (FUEL / INPUT / OUTPUT): the shared section-label style.
function sectionLabel(text: string): HTMLDivElement {
  const d = document.createElement('div');
  d.textContent = text.toUpperCase();
  d.style.cssText = css(labelCss(), 'margin:2px 0 1px 2px');
  return d;
}

// The grey flow arrow drawn between typed sections (fuel -> input -> output).
function sectionArrow(): HTMLDivElement {
  const d = document.createElement('div');
  d.textContent = '↓';
  d.style.cssText = css('text-align:center', `color:${SK.faint}`, 'font-size:15px', 'line-height:1', 'margin:-2px 0 -3px', `width:${SLOT}px`);
  return d;
}
